import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSubscription } from "@atlas/billing";
import { billingApiConfig } from "../../config";

interface ApiKeyState {
  hasKey: boolean;
  keyHint: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Mask an API key for display (show only last 4 characters)
 */
function maskApiKey(keyHint: string | null): string {
  if (!keyHint) return "";
  return `sk-...${keyHint}`;
}

/**
 * API key settings page for BYOK (Bring Your Own Key)
 * - Display masked key if one exists
 * - Form to enter new API key
 * - Save button validates and encrypts key via /user/api-key POST
 * - Remove button deletes key and redirects to billing if on BYOK tier
 * - Information about BYOK pricing
 */
export function ApiKeySettingsPage(): React.ReactElement {
  const navigate = useNavigate();
  const { subscription } = useSubscription({ apiConfig: billingApiConfig });

  const [keyState, setKeyState] = useState<ApiKeyState>({
    hasKey: false,
    keyHint: null,
    isLoading: true,
    error: null,
  });
  const [newKey, setNewKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch current API key status on mount
  useEffect(() => {
    async function fetchKeyStatus(): Promise<void> {
      try {
        const accessToken = await billingApiConfig.getAccessToken();
        const response = await fetch(`${billingApiConfig.baseUrl}/user/api-key`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          const data = (await response.json()) as {
            hasKey: boolean;
            keyHint: string | null;
          };
          setKeyState({
            hasKey: data.hasKey,
            keyHint: data.keyHint,
            isLoading: false,
            error: null,
          });
        } else if (response.status === 404) {
          // No key set
          setKeyState({
            hasKey: false,
            keyHint: null,
            isLoading: false,
            error: null,
          });
        } else {
          throw new Error("Failed to fetch API key status");
        }
      } catch (err) {
        setKeyState({
          hasKey: false,
          keyHint: null,
          isLoading: false,
          error: err instanceof Error ? err.message : "Failed to load API key status",
        });
      }
    }

    fetchKeyStatus();
  }, [billingApiConfig]);

  const handleSaveKey = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSuccessMessage(null);

    // Basic validation - Anthropic keys start with sk-ant-
    if (!newKey.startsWith("sk-ant-")) {
      setKeyState((prev) => ({
        ...prev,
        error: "Invalid API key format. Anthropic keys start with sk-ant-",
      }));
      return;
    }

    setIsSaving(true);
    setKeyState((prev) => ({ ...prev, error: null }));

    try {
      const accessToken = await billingApiConfig.getAccessToken();
      const response = await fetch(`${billingApiConfig.baseUrl}/user/api-key`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          apiKey: newKey,
          provider: "anthropic",
        }),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as {
          message?: string;
        };
        throw new Error(
          errorData.message ?? "Failed to save API key"
        );
      }

      // Success - update state
      setKeyState({
        hasKey: true,
        keyHint: newKey.slice(-4),
        isLoading: false,
        error: null,
      });
      setNewKey("");
      setSuccessMessage("API key saved successfully!");
    } catch (err) {
      setKeyState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : "Failed to save API key",
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveKey = async (): Promise<void> => {
    if (
      !confirm(
        "Are you sure you want to remove your API key? If you're on the BYOK plan, you'll need to upgrade to continue using Atlas."
      )
    ) {
      return;
    }

    setIsRemoving(true);
    setKeyState((prev) => ({ ...prev, error: null }));
    setSuccessMessage(null);

    try {
      const accessToken = await billingApiConfig.getAccessToken();
      const response = await fetch(`${billingApiConfig.baseUrl}/user/api-key`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to remove API key");
      }

      // Success - update state
      setKeyState({
        hasKey: false,
        keyHint: null,
        isLoading: false,
        error: null,
      });

      // If on BYOK tier, redirect to billing
      if (subscription?.tier === "byok") {
        navigate("/settings/billing", {
          state: { message: "API key removed. Please choose a plan to continue." },
        });
      } else {
        setSuccessMessage("API key removed successfully.");
      }
    } catch (err) {
      setKeyState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : "Failed to remove API key",
      }));
    } finally {
      setIsRemoving(false);
    }
  };

  if (keyState.isLoading) {
    return (
      <div className="px-6 py-5 max-w-xl mx-auto">
        <h1 className="text-lg font-semibold text-text-primary mb-5">API Key Settings</h1>
        <p className="text-sm text-text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <div className="px-6 py-5 max-w-xl mx-auto">
      <div className="mb-5">
        <Link
          to="/settings/billing"
          className="link text-xs"
        >
          &larr; Billing
        </Link>
      </div>

      <h1 className="text-lg font-semibold text-text-primary mb-4">API Key</h1>

      {keyState.error && (
        <div className="alert-error mb-3">
          {keyState.error}
        </div>
      )}

      {successMessage && (
        <div className="alert-success mb-3">
          {successMessage}
        </div>
      )}

      {/* BYOK Info Section */}
      <section className="card bg-bg mb-4">
        <h2 className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-2">
          Bring Your Own Key (BYOK)
        </h2>
        <p className="text-xs text-text-secondary mb-3">
          Use your own Anthropic API key for unlimited usage. Your key is
          encrypted at rest and never logged.
        </p>
        <ul className="text-xs text-text-secondary pl-4 space-y-0.5">
          <li>Pay Anthropic directly for usage</li>
          <li>No token limits from Atlas</li>
          <li>All Pro features included</li>
          <li>Key encrypted with AWS KMS</li>
        </ul>
      </section>

      {/* Current Key Status */}
      {keyState.hasKey && (
        <section className="card mb-4">
          <h2 className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-3">Current Key</h2>
          <div className="flex items-center justify-between">
            <code className="font-mono text-xs bg-bg rounded px-3 py-1.5 text-text-primary">
              {maskApiKey(keyState.keyHint)}
            </code>
            <button
              onClick={handleRemoveKey}
              disabled={isRemoving}
              className="btn-danger"
            >
              {isRemoving ? "Removing..." : "Remove"}
            </button>
          </div>
        </section>
      )}

      {/* Add/Update Key Form */}
      <section className="card mb-4">
        <h2 className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-3">
          {keyState.hasKey ? "Update Key" : "Add Key"}
        </h2>
        <p className="text-xs text-text-secondary mb-3">
          Enter your Anthropic API key. Find it at{" "}
          <a
            href="https://console.anthropic.com/settings/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="link"
          >
            console.anthropic.com
          </a>
        </p>

        <form onSubmit={handleSaveKey}>
          <div className="mb-3">
            <label
              htmlFor="apiKey"
              className="block mb-1.5 text-xs font-medium text-text-secondary"
            >
              API Key
            </label>
            <input
              id="apiKey"
              type="password"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="sk-ant-..."
              required
              className="input-field font-mono"
            />
          </div>

          <button
            type="submit"
            disabled={isSaving || !newKey}
            className="btn-primary"
          >
            {isSaving ? "Validating..." : "Save Key"}
          </button>
        </form>
      </section>

      {/* Warning for BYOK users */}
      {subscription?.tier === "byok" && !keyState.hasKey && (
        <div className="alert-warning">
          <strong>Warning:</strong> You are on the BYOK plan but don't have an
          API key configured. You won't be able to use Atlas features until you
          add a valid API key or{" "}
          <Link to="/settings/billing" className="underline">
            upgrade to a paid plan
          </Link>
          .
        </div>
      )}
    </div>
  );
}
